import React, { useState, useEffect, useRef } from "react";
import { Modal } from "@/components/ui/Modal";
import { useAppStore } from "@/core/store/useAppStore";
import { Collection } from "@/services/collectionsService";
import * as collectionsService from "@/services/collectionsService";
import { SpinnerIcon, CheckIcon } from "@/components/icons";
import { EntityCreateButton } from "@/core/entityConfig";

interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  selectedIds: number[];
  onComplete: () => void;
}

export const AddToCollectionModal: React.FC<AddToCollectionModalProps> = ({
  isOpen,
  onClose,
  clientId,
  selectedIds,
  onComplete,
}) => {
  const {
    collections: collectionsState,
    fetchCollections,
    createCollection,
  } = useAppStore();

  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  const { collections, isLoading } = collectionsState;

  useEffect(() => {
    if (isOpen && clientId) {
      fetchCollections(clientId);
      setSelectedCollectionId(null);
      setIsCreating(false);
      setNewName("");
    }
  }, [isOpen, clientId, fetchCollections]);

  useEffect(() => {
    if (isCreating && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [isCreating]);

  const handleCreateAndAdd = async () => {
    if (!newName.trim()) return;

    setAdding(true);
    try {
      const collection = await createCollection(clientId, {
        name: newName.trim(),
      });
      await collectionsService.addItemsToCollection(
        clientId,
        collection.id,
        selectedIds,
      );
      onComplete();
    } catch (err) {
      console.error("Failed to create collection and add items:", err);
    } finally {
      setAdding(false);
    }
  };

  const handleAddToExisting = async () => {
    if (!selectedCollectionId) return;

    setAdding(true);
    try {
      await collectionsService.addItemsToCollection(
        clientId,
        selectedCollectionId,
        selectedIds,
      );
      onComplete();
    } catch (err) {
      console.error("Failed to add items to collection:", err);
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateAndAdd();
    } else if (e.key === "Escape") {
      setIsCreating(false);
      setNewName("");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add ${selectedIds.length} item${selectedIds.length !== 1 ? "s" : ""} to collection`}
      size="sm"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          {!isCreating && selectedCollectionId && (
            <button
              onClick={handleAddToExisting}
              disabled={adding}
              className="px-4 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {adding ? (
                <>
                  <SpinnerIcon className="w-4 h-4" />
                  Adding...
                </>
              ) : (
                "Add to Collection"
              )}
            </button>
          )}
        </>
      }
    >
      <div className="p-4 space-y-3">
        {/* Create new collection */}
        {isCreating ? (
          <div className="flex gap-2">
            <input
              ref={createInputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Collection name..."
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            <button
              onClick={handleCreateAndAdd}
              disabled={!newName.trim() || adding}
              className="px-3 py-2 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {adding ? (
                <SpinnerIcon className="w-4 h-4" />
              ) : (
                "Create & Add"
              )}
            </button>
            <button
              onClick={() => {
                setIsCreating(false);
                setNewName("");
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <EntityCreateButton
            entity="collection"
            onClick={() => setIsCreating(true)}
            variant="secondary"
            className="w-full"
          />
        )}

        {/* Divider */}
        {collections.length > 0 && (
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">or choose existing</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        )}

        {/* Existing collections */}
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <SpinnerIcon className="w-5 h-5 text-gray-400" />
          </div>
        ) : collections.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            No collections yet. Create one above.
          </p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {collections.map((collection: Collection) => (
              <button
                key={collection.id}
                onClick={() => setSelectedCollectionId(
                  selectedCollectionId === collection.id ? null : collection.id
                )}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all ${
                  selectedCollectionId === collection.id
                    ? "bg-cyan-50 border border-cyan-300 text-cyan-900"
                    : "bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 shrink-0 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                  </svg>
                  <span className="text-sm font-medium truncate">
                    {collection.name || "Untitled"}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-400">
                    {collection.itemCount} items
                  </span>
                  {selectedCollectionId === collection.id && (
                    <CheckIcon className="w-4 h-4 text-cyan-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
